import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Globe, Instagram, Facebook, Twitter, Tag, Share2,
  Loader2, AlertCircle, User, ExternalLink, Mail, ShoppingBag,
  Calendar, MapPin, Package, UserPlus, UserCheck
} from 'lucide-react';
import { VendorPublicProfile, VendorRatingSummary, VendorPublicProductItem, VendorUpcomingFair } from '../types';
import { vendorApi, vendorReviewApi, authApi, api } from '../services/api';
import VendorPortfolioGallery from './VendorPortfolioGallery';
import VendorBadgeDisplay from './VendorBadgeDisplay';
import VendorRatingDistribution from './VendorRatingDistribution';
import VendorRatingBadge from './VendorRatingBadge';
import VendorReviewsList from './VendorReviewsList';
import SEOHead from './SEOHead';
import { getVendorBusinessSchema } from '../utils/structuredData';
import { extractApiError } from '../utils/errorUtils';
import { formatCurrency } from '../utils/currency';
import { formatDateOnly, parseDateOnly } from '../utils/fairStatus'; // Issue #1672
import { FeatureFlag } from '../hooks/useFeatureFlag';

export default function VendorProfileView() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState<VendorPublicProfile | null>(null);
  const [ratingSummary, setRatingSummary] = useState<VendorRatingSummary | null>(null);
  const [products, setProducts] = useState<VendorPublicProductItem[]>([]);
  const [upcomingFairs, setUpcomingFairs] = useState<VendorUpcomingFair[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadVendor = async () => {
      if (!vendorId) {
        if (isMounted) {
          setError('Invalid vendor ID');
          setIsLoading(false);
        }
        return;
      }

      const id = parseInt(vendorId, 10);
      if (isNaN(id)) {
        if (isMounted) {
          setError('Invalid vendor ID');
          setIsLoading(false);
        }
        return;
      }

      try {
        if (isMounted) {
          setIsLoading(true);
          setError(null);
        }
        const data = await vendorApi.getVendorPublicProfile(id);
        if (isMounted) {
          setVendor(data);
        }
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load vendor:', apiError);
        if (isMounted) {
          if (apiError.status === 404) {
            setError('Vendor not found');
          } else {
            setError(apiError.message);
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadVendor();

    return () => {
      isMounted = false;
    };
  }, [vendorId]);

  // Load rating summary (Issue #957)
  useEffect(() => {
    let isMounted = true;

    // Clear stale rating summary when vendorId changes
    setRatingSummary(null);

    const loadRatingSummary = async () => {
      if (!vendorId) return;

      const id = parseInt(vendorId, 10);
      if (isNaN(id)) return;

      try {
        const summary = await vendorReviewApi.getRatingSummary(id);
        if (isMounted) {
          setRatingSummary(summary);
        }
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load rating summary:', apiError);
        // Don't show error to user - rating summary is optional
        if (isMounted) {
          setRatingSummary(null);
        }
      }
    };

    loadRatingSummary();

    return () => {
      isMounted = false;
    };
  }, [vendorId]);

  // Load public product showcase (Issue #1200)
  useEffect(() => {
    let isMounted = true;

    const loadProducts = async () => {
      if (!vendorId) return;
      const id = parseInt(vendorId, 10);
      if (isNaN(id)) return;

      try {
        const data = await vendorApi.getVendorPublicProducts(id);
        if (isMounted) setProducts(data);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load vendor products:', apiError);
        // Non-fatal — showcase is optional
        if (isMounted) setProducts([]);
      }
    };

    loadProducts();
    return () => { isMounted = false; };
  }, [vendorId]);

  // Load upcoming fair appearances (Issue #1200)
  useEffect(() => {
    let isMounted = true;

    const loadUpcomingFairs = async () => {
      if (!vendorId) return;
      const id = parseInt(vendorId, 10);
      if (isNaN(id)) return;

      try {
        const data = await vendorApi.getVendorUpcomingFairs(id);
        if (isMounted) setUpcomingFairs(data);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load vendor upcoming fairs:', apiError);
        if (isMounted) setUpcomingFairs([]);
      }
    };

    loadUpcomingFairs();
    return () => { isMounted = false; };
  }, [vendorId]);

  // Load follow status for authenticated users (Issue #1200, #1472)
  // Only load when vendor_following feature flag is enabled
  useEffect(() => {
    let isMounted = true;

    const loadFollowStatus = async () => {
      if (!authApi.isLoggedIn()) {
        if (isMounted) setIsAuthenticated(false);
        return;
      }
      if (isMounted) setIsAuthenticated(true);

      const id = vendorId ? parseInt(vendorId, 10) : NaN;
      if (isNaN(id)) return;

      try {
        // Check if vendor_following feature flag is enabled before loading followed vendors
        const flagResponse = await api.get('/feature-flags/vendor_following');
        const isFollowingEnabled = flagResponse.data?.is_enabled || false;

        if (!isFollowingEnabled) {
          // Feature is disabled, skip loading follow status
          return;
        }

        const { vendor_ids } = await vendorApi.getFollowedVendors();
        if (isMounted) setIsFollowing(vendor_ids.includes(id));
      } catch {
        // Non-fatal — follow status just won't show
      }
    };

    loadFollowStatus();
    return () => { isMounted = false; };
  }, [vendorId]);

  const handleFollowToggle = async () => {
    const id = vendorId ? parseInt(vendorId, 10) : NaN;
    if (isNaN(id)) return;

    if (!isAuthenticated) {
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }

    setIsFollowLoading(true);
    try {
      if (isFollowing) {
        await vendorApi.unfollowVendor(id);
        setIsFollowing(false);
      } else {
        await vendorApi.followVendor(id);
        setIsFollowing(true);
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to toggle follow:', apiError);
    } finally {
      setIsFollowLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = vendor ? `${vendor.business_name} - Local Artist Fairs` : 'Vendor Profile';

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
        // User cancelled or error
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(url);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      } catch {
        // Fallback failed
      }
    }
  };

  const hasSocialLinks = vendor && (
    vendor.website ||
    vendor.social_media_instagram ||
    vendor.social_media_facebook ||
    vendor.social_media_twitter
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'Vendor not found'}
          </h2>
          <p className="text-gray-600 mb-4">
            The vendor profile you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/vendors"
            className="inline-flex items-center gap-2 text-secondary hover:text-primary-dark font-medium"
          >
            Browse all vendors
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  // Generate SEO content
  const seoTitle = vendor.business_name;
  const seoDescription = vendor.description || `View ${vendor.business_name}'s profile on Local Artist Fairs. ${vendor.product_types ? vendor.product_types.join(', ') : ''}`;
  const seoKeywords = vendor.product_types ? vendor.product_types.join(', ') : 'local vendor, artisan, handmade';
  const canonicalUrl = `https://localartistfairs.com/vendors/${vendor.id}`;
  const ogImage = vendor.portfolio_images && vendor.portfolio_images.length > 0
    ? vendor.portfolio_images[0]
    : undefined;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={canonicalUrl}
        ogImage={ogImage}
        ogType="business.business"
        structuredData={getVendorBusinessSchema(vendor)}
      />
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to vendors
        </button>

      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
        {/* Cover/Hero Area */}
        <div className="h-32 md:h-48 bg-gradient-to-r from-teal-500 to-teal-600 relative">
          {vendor.portfolio_images && vendor.portfolio_images.length > 0 && (
            <img
              src={vendor.portfolio_images[0]}
              alt=""
              className="w-full h-full object-cover opacity-30"
            />
          )}
        </div>

        {/* Profile Info */}
        <div className="px-6 pb-6">
          {/* Avatar */}
          <div className="-mt-16 mb-4 relative z-10">
            <div className="w-32 h-32 rounded-full border-4 border-white bg-white shadow-lg overflow-hidden">
              {vendor.portfolio_images && vendor.portfolio_images.length > 0 ? (
                <img
                  src={vendor.portfolio_images[0]}
                  alt={vendor.business_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center">
                  <User className="w-16 h-16 text-teal-400" />
                </div>
              )}
            </div>
          </div>

          {/* Name and Actions */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{vendor.business_name}</h1>
              {vendor.created_at && (
                <p className="text-gray-500 text-sm mt-1">
                  Member since {new Date(vendor.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <FeatureFlag flagKey="vendor_following" fallback={null}>
                <button
                  onClick={handleFollowToggle}
                  disabled={isFollowLoading}
                  data-testid="follow-button"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    isFollowing
                      ? 'border-primary bg-teal-50 text-primary-dark hover:bg-secondary-light/100'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  } ${isFollowLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label={isFollowing ? 'Unfollow vendor' : 'Follow vendor'}
                >
                  {isFollowing
                    ? <UserCheck className="w-4 h-4" />
                    : <UserPlus className="w-4 h-4" />
                  }
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              </FeatureFlag>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
              >
                <Share2 className="w-4 h-4" />
                {shareSuccess ? 'Link copied!' : 'Share'}
              </button>
              {vendor.website && (
                <a
                  href={vendor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  <Mail className="w-4 h-4" />
                  Contact
                </a>
              )}
            </div>
          </div>

          {/* Product Types */}
          {vendor.product_types && vendor.product_types.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {vendor.product_types.map(type => (
                <span
                  key={type}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-primary-dark rounded-full text-sm"
                >
                  <Tag className="w-3 h-3" />
                  {type}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - About & Links */}
        <div className="lg:col-span-1 space-y-6">
          {/* About Section */}
          {(vendor.description || vendor.bio) && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">About</h2>
              {vendor.description && (
                <p className="text-gray-700 mb-4">{vendor.description}</p>
              )}
              {vendor.bio && (
                <div className="text-gray-600 whitespace-pre-line">{vendor.bio}</div>
              )}
            </div>
          )}

          {/* Social Links */}
          {hasSocialLinks && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Connect</h2>
              <div className="space-y-3">
                {vendor.website && (
                  <a
                    href={vendor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-700 hover:text-secondary transition-colors"
                  >
                    <Globe className="w-5 h-5" />
                    <span className="truncate">{new URL(vendor.website).hostname}</span>
                    <ExternalLink className="w-4 h-4 ml-auto text-gray-400" />
                  </a>
                )}
                {vendor.social_media_instagram && (
                  <a
                    href={vendor.social_media_instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-700 hover:text-pink-600 transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                    <span>Instagram</span>
                    <ExternalLink className="w-4 h-4 ml-auto text-gray-400" />
                  </a>
                )}
                {vendor.social_media_facebook && (
                  <a
                    href={vendor.social_media_facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-700 hover:text-secondary transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                    <span>Facebook</span>
                    <ExternalLink className="w-4 h-4 ml-auto text-gray-400" />
                  </a>
                )}
                {vendor.social_media_twitter && (
                  <a
                    href={vendor.social_media_twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-700 hover:text-blue-400 transition-colors"
                  >
                    <Twitter className="w-5 h-5" />
                    <span>Twitter / X</span>
                    <ExternalLink className="w-4 h-4 ml-auto text-gray-400" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Achievement Badges (Issue #684, #955) */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Achievements</h2>
            <VendorBadgeDisplay vendorId={parseInt(vendorId!, 10)} />
          </div>
        </div>

        {/* Right Column - Portfolio */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Portfolio</h2>
            <VendorPortfolioGallery
              images={vendor.portfolio_images || []}
              businessName={vendor.business_name}
            />
          </div>
        </div>
      </div>

      {/* Product Showcase (Issue #1200) */}
      {products.length > 0 && (
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="product-showcase">
          <div className="flex items-center gap-2 mb-6">
            <ShoppingBag className="w-5 h-5 text-secondary" />
            <h2 className="text-xl font-bold text-gray-900">Products</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {products.map(product => (
              <div
                key={product.id}
                className="bg-gray-50 rounded-lg overflow-hidden border border-gray-100 flex flex-col"
                data-testid="product-card"
              >
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-32 object-cover"
                  />
                ) : (
                  <div className="w-full h-32 bg-teal-50 flex items-center justify-center">
                    {product.emoji ? (
                      <span className="text-4xl" role="img" aria-label={product.name}>{product.emoji}</span>
                    ) : (
                      <Package className="w-10 h-10 text-teal-300" />
                    )}
                  </div>
                )}
                <div className="p-3 flex flex-col flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                  {product.category && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{product.category}</p>
                  )}
                  <div className="mt-auto pt-2 flex items-center justify-between">
                    {product.base_price != null ? (
                      <span className="text-sm font-semibold text-primary-dark">
                        {formatCurrency(product.base_price)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Price varies</span>
                    )}
                    {product.is_made_to_order && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">MTO</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Fairs (Issue #1200) */}
      {upcomingFairs.length > 0 && (
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="upcoming-fairs">
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="w-5 h-5 text-secondary" />
            <h2 className="text-xl font-bold text-gray-900">Where to Find Us</h2>
          </div>
          <div className="space-y-3">
            {upcomingFairs.map(fair => (
              <Link
                key={fair.id}
                to={`/fairs/${fair.id}`}
                className="flex items-center gap-4 p-4 rounded-lg border border-gray-100 hover:border-teal-300 hover:bg-secondary-light/50 transition-colors group"
                data-testid="upcoming-fair-item"
              >
                {fair.image ? (
                  <img
                    src={fair.image}
                    alt={fair.name}
                    className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-teal-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 group-hover:text-primary-dark truncate">{fair.name}</p>
                  {(fair.city || fair.state) && (
                    <p className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      {[fair.city, fair.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                  <p className="text-sm text-secondary font-medium mt-1">
                    {formatDateOnly(fair.start_date, { month: 'short', day: 'numeric' })}
                    {fair.start_date !== fair.end_date && (
                      <> – {formatDateOnly(fair.end_date, { month: 'short', day: 'numeric', year: 'numeric' })}</>
                    )}
                    {fair.start_date === fair.end_date && (
                      <>, {parseDateOnly(fair.start_date)?.getFullYear()}</>
                    )}
                  </p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-teal-500 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Reviews Section (Issue #956) */}
      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6" id="reviews">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Reviews</h2>
          <VendorRatingBadge
            vendorId={parseInt(vendorId!, 10)}
            averageRating={ratingSummary?.average_rating}
            totalReviews={ratingSummary?.total_reviews}
            size="lg"
          />
        </div>

        {/* Rating Distribution */}
        {ratingSummary && (
          <div className="mb-8">
            <VendorRatingDistribution
              vendorId={parseInt(vendorId!, 10)}
              ratingDistribution={ratingSummary.rating_distribution}
              totalReviews={ratingSummary.total_reviews}
              verifiedCount={ratingSummary.verified_reviews_count}
              onFilterChange={setRatingFilter}
            />
          </div>
        )}

        {/* Reviews List */}
        <VendorReviewsList
          vendorId={parseInt(vendorId!, 10)}
          ratingFilter={ratingFilter}
        />
      </div>
    </div>
    </>
  );
}
